import type {Where} from 'payload'

import type {FreeResource, FreeResourcesPage} from '~/types/legacy'
import type {Populated} from '~/types/populated'
import {extractDocs} from '~/utils/transform-payload'

export function useFreeResources() {
  const sdk = usePayloadSdk()
  const route = useRoute()

  const allLoadedResources = ref<Populated<FreeResource>[]>([])

  const allResources = computed(() => allLoadedResources.value)

  const fetchPageData = async () => {
    const result = await sdk.findGlobal({
      slug: 'freeResourcesGlobal',
      depth: 2,
      draft: !!route.query.preview,
    })

    return result as Populated<FreeResourcesPage>
  }

  const fetchAllResources = async (categoryFilter?: string) => {
    const where: Where = {
      _status: {equals: 'published'},
    }

    if (categoryFilter && categoryFilter !== 'all') {
      where.category = {
        equals: categoryFilter,
      }
    }

    const response = await sdk.find({
      collection: 'freeResources',
      depth: 2,
      where,
      sort: '-createdAt',
      limit: 1000,
      draft: !!route.query.preview,
    })

    const resources = extractDocs(response)
    allLoadedResources.value = resources
  }

  const fetch = async () => {
    const pageData = await fetchPageData()
    await fetchAllResources()

    return {
      ...pageData,
      resources: {
        freeResources: allLoadedResources.value,
      },
    }
  }

  const fetchBySlug = async (slug: string) => {
    const response = await sdk.find({
      collection: 'freeResources',
      depth: 3,
      where: {
        slug: {equals: slug},
      },
      limit: 1,
      draft: !!route.query.preview,
    })

    const resources = extractDocs(response)
    const resource = resources[0]

    if (!resource) return null

    return resource
  }

  const filterByCategory = async (category: string) => {
    await fetchAllResources(category)
  }

  return {
    fetch,
    fetchPageData,
    fetchBySlug,
    filterByCategory,
    allResources,
  }
}
